import React from 'react';
import { useSelector } from 'react-redux';
import { Popover, Button, Form, Radio, Space } from 'antd';
import { useTranslation } from 'react-i18next';
import { useForm, FormProvider } from 'react-hook-form';
import { ApplicationState } from '../../../reducers';
import { StandardResourceCodeAuth } from '../../../../types/resources';

type SourceFilterPopoverProps = {
  children: React.ReactNode;
  sourceFilterId?: number;
  handleChange: (sourceId?: number) => void;
};

const SourceFilterPopover: React.FC<SourceFilterPopoverProps> = props => {
  const { t } = useTranslation();

  const [popoverVisible, setPopoverVisible] = React.useState(false);

  const receiverSources = useSelector(
    (state: ApplicationState) => state.resources.data.receiver.external_data_providers
  );

  const formMethods = useForm<{ sourceId?: number }>({
    defaultValues: {
      sourceId: props.sourceFilterId,
    },
    mode: 'onTouched',
  });

  const { handleSubmit, setValue, watch, formState, reset } = formMethods;
  const sourceId = watch('sourceId');

  React.useEffect(() => {
    if (!popoverVisible) reset({ sourceId: props.sourceFilterId });
  }, [popoverVisible, props.sourceFilterId, reset]);

  const handleVisibleChange = (visible: boolean) => {
    setPopoverVisible(visible);
  };

  const onSubmit = (values: { sourceId?: number }) => {
    props.handleChange(values.sourceId);
    handleVisibleChange(false);
  };

  return (
    <Popover
      open={popoverVisible}
      onOpenChange={visible => handleVisibleChange(visible)}
      overlayClassName="receiver__source-filter-popover"
      placement="bottomLeft"
      destroyTooltipOnHide
      content={
        <FormProvider {...formMethods}>
          <Form layout="vertical" onFinish={handleSubmit(onSubmit)}>
            <div className="p-3">
              <Radio.Group
                onChange={e => setValue('sourceId', e.target.value, { shouldDirty: true })}
                value={sourceId}
              >
                <Space direction="vertical">
                  {receiverSources.map((source: StandardResourceCodeAuth) => (
                    <Radio value={source.id} key={source.id}>
                      {source.name}
                    </Radio>
                  ))}
                </Space>
              </Radio.Group>
            </div>

            <div className="flex-col items-end p-3">
              <div>
                <Button
                  onClick={() => {
                    setValue('sourceId', undefined);
                    handleSubmit(onSubmit)();
                  }}
                  className="drawer-submit__bottom-cancel"
                >
                  {t('common:reset')}
                </Button>
                <Button
                  type="primary"
                  htmlType="submit"
                  disabled={!formState.isDirty || formState.isSubmitting}
                >
                  {t('common:ok')}
                </Button>
              </div>
            </div>
          </Form>
        </FormProvider>
      }
      trigger="click"
    >
      {props.children}
    </Popover>
  );
};

export default SourceFilterPopover;
